/**
 * Strandskydd – 4 underrubriker.
 */
export const strandskyddRules = [
  {
    slug: "100-metersregeln",
    title: "Strandskydd 100-metersregeln",
    h1: "Strandskydd – 100-metersregeln förklarad",
    metaDescription:
      "Strandskyddet gäller 100 m från strandlinjen (ibland utökat till 300 m). Så mäts avståndet och när gäller undantag.",
    keywords: "strandskydd 100 meter, strandskydd 300 meter, strandlinje",
    intro:
      "Strandskyddet är 100 m från strandlinjen både uppåt land och ut i vattnet. På vissa platser är det utökat till 300 m via länsstyrelsens beslut.",
    verified_source: { sfs: "1998:808", chapter: "7", paragraph: "14" },
    excerpt: {
      text: "Strandskyddet omfattar land- och vattenområdet intill 100 meter från strandlinjen vid normalt medelvattenstånd.",
      source: "Miljöbalken 7 kap. 14 §",
    },
    examples: [
      { type: "Villa", title: "Uppmätning", text: "Mät från strandlinjen vid normalt medelvattenstånd – inte vid högvatten. Använd sjökort eller lantmäteriets kartor." },
      { type: "Attefall", title: "Utökat område", text: "Om länsstyrelsen beslutat om 300 m gäller det istället för 100 m – kolla länsstyrelsens beslut för din kommun." },
    ],
    mistakes: [
      "Att mäta från strandkanten istället för normalt medelvattenstånd.",
      "Att glömma att strandskyddet gäller kring alla sjöar och vattendrag – även små bäckar.",
    ],
    municipality_variations:
      "I vissa kommuner (t.ex. Stockholm skärgård) är 300 m regel vid hav och stora sjöar. Kolla länsstyrelsens digitala karta.",
    faq: [
      { q: "Gäller strandskydd runt små bäckar?", a: "Ja, som huvudregel gäller det alla sjöar och vattendrag men små diken/rännilar undantas ofta." },
      { q: "Kan strandskydd upphävas?", a: "Ja, i landsbygdsutvecklingsområden (LIS) kan kommunen upphäva strandskyddet via detaljplan." },
    ],
    related: ["strandskydd/sarskilda-skal", "strandskydd/dispensprocess", "attefall/attefall-strandskydd"],
    updated: "2026-07-04",
  },
  {
    slug: "sarskilda-skal",
    title: "Strandskydd – sex särskilda skäl",
    h1: "Strandskyddsdispens – sex särskilda skäl (Miljöbalken 7:18c)",
    metaDescription:
      "För att få dispens från strandskyddet krävs särskilda skäl. De sex lagliga skälen förklarade med exempel.",
    keywords: "strandskyddsdispens, särskilda skäl, 7:18c",
    intro:
      "För att få dispens från strandskyddet måste ett av sex särskilda skäl vara uppfyllt – och ingen får utestängas från allemansrättsligt tillgängligt område.",
    verified_source: { sfs: "1998:808", chapter: "7", paragraph: "18c" },
    excerpt: {
      text: "Som särskilda skäl vid prövningen av en fråga om upphävande av eller dispens från strandskyddet får man beakta endast om det område som upphävandet eller dispensen avser: 1. redan tagits i anspråk, 2. genom en väg... [6 skäl totalt]",
      source: "Miljöbalken 7 kap. 18c §",
    },
    examples: [
      { type: "Villa", title: "Redan ianspråktaget", text: "Skäl 1: På en tomt med befintligt bostadshus kan du få dispens för attefallshus eftersom området redan är ianspråktaget." },
      { type: "Attefall", title: "Angelägen samhällsintresse", text: "Skäl 5: Skola, äldreboende eller allmän anläggning som inte kan placeras utanför strandskydd." },
    ],
    mistakes: [
      "Att argumentera med skäl som inte finns med i lagen – 'jag vill bara ha utsikt' räcker inte.",
      "Att glömma att även allemansrätten måste bevaras – dispens som stänger av strand nekas.",
    ],
    municipality_variations:
      "Vissa kommuner är strängare i tolkningen av 'redan ianspråktaget' – t.ex. Stockholm skärgård.",
    faq: [
      { q: "Vad är LIS-område?", a: "Landsbygdsutvecklingsområde – utpekade zoner där dispens ges lättare för att stimulera glesbygdsutveckling." },
      { q: "Kan jag överklaga avslag?", a: "Ja, till länsstyrelsen inom 3 veckor. Sedan mark- och miljödomstolen." },
    ],
    related: ["strandskydd/100-metersregeln", "strandskydd/dispensprocess"],
    updated: "2026-07-04",
  },
  {
    slug: "dispensprocess",
    title: "Strandskyddsdispens – ansökningsprocess",
    h1: "Strandskyddsdispens – så söker du steg för steg",
    metaDescription:
      "Ansökan om strandskyddsdispens: handlingar, handläggningstid, kostnad och överklagande. Guide 2026.",
    keywords: "ansöka strandskyddsdispens, dispens strandskydd handlingar",
    intro:
      "Ansökan om strandskyddsdispens görs hos kommunen (eller länsstyrelsen om delegation saknas). Här är hela processen.",
    howto: {
      name: "Så söker du strandskyddsdispens",
      steps: [
        { name: "1. Kontrollera zonen", text: "Är fastigheten inom strandskyddat område? Använd länsstyrelsens karta." },
        { name: "2. Identifiera särskilt skäl", text: "Välj vilket av de sex skälen i MB 7:18c som är tillämpligt." },
        { name: "3. Ta fram handlingar", text: "Situationsplan (skala 1:400), ritningar, motivering till särskilt skäl, tomtplansavgränsning." },
        { name: "4. Ansök", text: "Skicka in till kommunens miljö- eller byggnadsnämnd." },
        { name: "5. Handläggning", text: "Beslut inom 4-12 veckor. Länsstyrelsen kan överpröva kommunens beslut." },
      ],
    },
    examples: [
      { type: "Villa", title: "Attefall på strandtomt", text: "Ansöker om dispens för 30 m² attefallshus. Motivering: skäl 1 (redan ianspråktagen tomt)." },
    ],
    mistakes: [
      "Att inte inkludera tomtplansavgränsning – kommunen kan inte pröva utan det.",
      "Att kombinera dispens med bygglovsansökan för tidigt – dispens måste vara i kraft innan bygglov söks.",
    ],
    municipality_variations:
      "Vissa kommuner har snabbare handläggningstid (4 v) för dispens, andra tar upp till 3 månader.",
    faq: [
      { q: "Vad kostar strandskyddsdispens?", a: "Varierar mellan 5 000-15 000 kr beroende på kommun." },
      { q: "Kan grannen överklaga min dispens?", a: "Ja, sakägare (grannar, boende) har överklaganderätt inom 3 veckor från kungörelse." },
    ],
    related: ["strandskydd/sarskilda-skal", "strandskydd/100-metersregeln"],
    updated: "2026-07-04",
  },
  {
    slug: "lis-omraden",
    title: "LIS-områden (Landsbygdsutveckling i strandnära lägen)",
    h1: "LIS-områden – strandskyddsdispens för landsbygden",
    metaDescription: "I LIS-områden är strandskyddsdispens lättare att få för att främja landsbygdsutveckling. Se hur du hittar din kommuns LIS-plan.",
    keywords: "LIS-område, landsbygdsutveckling strandskydd, strandskydd landsbygd",
    intro: "LIS (Landsbygdsutveckling i strandnära lägen) är en möjlighet för glesbygdskommuner att peka ut områden där strandskyddsdispens är lättare att få, för att främja bostäder och verksamheter.",
    measurements: [
      { label: "Grund", value: "Miljöbalken 7 kap. 18d §" },
      { label: "Krävs", value: "LIS-område utpekat i kommunens översiktsplan" },
      { label: "Storstadsområden", value: "Ingår oftast INTE i LIS" },
    ],
    examples: [
      { type: "Villa", title: "Nybygge i LIS-område", text: "Enklare att få dispens – räcker med att åtgärden bidrar till landsbygdsutveckling." },
      { type: "Företag", title: "Sjönära campingplats", text: "LIS + särskilda skäl → dispens ofta beviljad." },
    ],
    mistakes: [
      "Att tro att LIS = automatisk dispens – man måste fortfarande ansöka och motivera.",
    ],
    municipality_variations: "Alla kommuner har inte LIS-områden. Kolla din kommuns översiktsplan för att se om det finns.",
    faq: [
      { q: "Hur hittar jag om min fastighet är i LIS-område?", a: "Kolla kommunens översiktsplan eller ring stadsbyggnadskontoret." },
    ],
    related: ["strandskydd/sarskilda-skal", "strandskydd/dispensprocess"],
    updated: "2026-07-04",
  },
  {
    slug: "strandskydd-brygga",
    title: "Bygga brygga vid vatten – strandskydd",
    h1: "Brygga och strandskydd – dispens och regler",
    metaDescription: "En egen brygga kräver strandskyddsdispens och ofta bygglov. Regler för längd, bredd, gemensam brygga och vinterförvaring 2026.",
    keywords: "bygga brygga, brygga strandskydd, dispens brygga",
    intro: "Alla bryggor inom strandskyddat område (100 m) kräver dispens. Storleken och antalet båtplatser påverkar möjligheten att få tillstånd.",
    measurements: [
      { label: "Dispens", value: "Krävs alltid inom 100 m från vatten" },
      { label: "Bygglov", value: "Krävs för brygga > 25 m² eller 15 m lång" },
      { label: "Anmälan Vattenverksamhet", value: "Krävs enligt Miljöbalken 11 kap." },
    ],
    examples: [
      { type: "Villa", title: "Liten enskild brygga 3x8 m", text: "Dispens + vattenverksamhet-anmälan räcker. Ingen bygglov." },
      { type: "Gemensam", title: "Samfällighetsbrygga 20 platser", text: "Dispens + bygglov + miljödom kan krävas." },
    ],
    mistakes: [
      "Att bygga brygga utan vattenverksamhets-anmälan till länsstyrelsen.",
      "Att förvara båten på tomten under strandskyddet – kan tolkas som ny bygglovsfri åtgärd.",
    ],
    municipality_variations: "Skärgårdskommuner (t.ex. Värmdö) har oftare gemensamma bryggor godkända.",
    faq: [
      { q: "Får jag reparera min gamla brygga?", a: "Renovering utan förstoring är ofta OK utan dispens – men kontakta länsstyrelsen först." },
    ],
    related: ["strandskydd/dispensprocess", "strandskydd/100-metersregeln"],
    updated: "2026-07-04",
  },
  {
    slug: "strandskydd-tomtplans-avgransning",
    title: "Tomtplansavgränsning vid strandskydd",
    h1: "Tomtplansavgränsning – hur långt sträcker sig din 'privata' yta?",
    metaDescription: "Tomtplansavgränsningen bestämmer var allemansrätten börjar även på din tomt. Central del av strandskyddsprövningen.",
    keywords: "tomtplansavgränsning, tomtplats strandskydd, privatiserad yta",
    intro: "Vid strandskyddsdispens fastställer kommunen en tomtplatsavgränsning – hur stor del av tomten som får privatiseras (staket, gräsmatta, uteplats). Utanför denna gräns gäller allemansrätten.",
    measurements: [
      { label: "Typisk storlek", value: "300-1500 m² beroende på kommun" },
      { label: "Ingår", value: "Byggnader, uteplats, privatiserad gräsmatta" },
      { label: "Ingår INTE", value: "Skogsmark, klippstrand, naturmark" },
    ],
    examples: [
      { type: "Villa", title: "Sjötomt 3000 m²", text: "Tomtplats sätts oftast till 800-1200 m² kring huset – resten öppet för allemansrätt." },
    ],
    mistakes: [
      "Att sätta staket utanför tomtplansavgränsningen – strider mot strandskyddet.",
      "Att klippa gräs långt utanför tomten – kan tolkas som privatisering.",
    ],
    municipality_variations: "Vissa kommuner tillåter större tomtplatser, andra strikt 300 m².",
    faq: [
      { q: "Kan tomtplansavgränsningen ändras?", a: "Ja, genom ny dispensansökan – men det är svårt att utöka den." },
    ],
    related: ["strandskydd/dispensprocess", "strandskydd/sarskilda-skal"],
    updated: "2026-07-04",
  },
];
